import { Location } from "history";
import produce from "immer";
import { useLocation } from "react-router";
import { TableRowCreateQueryParams } from "src/components/pages/TableRowCreate/types";
import {
  useHistoryPush,
  useLocationHistory,
} from "src/components/providers/LocationHistoryProvider";
import { getGoBackUrl } from "src/components/templates/TableRowDetail/utils";
import { TableRowQueryParams } from "src/components/templates/TableRowMultiDetail/MultiDetailsItems/ItemTemplatesHeader/utils";
import { whereItemPLACEHOLDER } from "src/requests/sharedTypes/placeholderData";
import { PAGING_OPTION_VALUES } from "../components/organisms/TemplateView/fragments/TemplateTable/Controls/constants";
import {
  createAdaptationPlanRootLink,
  createAnnualEvalRootLink,
  createPersonalDevPlanRootLink,
  createPotentialEvalRootLink,
} from "../components/pages/Hwr/components/pages/hwrLinksCreator";
import { EMPTY_SOR } from "../globalConsts";
import {
  DataSourceId,
  Gate,
  RelationType,
  SOR,
  TemplateId,
  TemplateType,
  VoidFnc,
  WhereItem,
} from "../globalTypes";
import { useNotificationRowKeysForTemplate } from "../store/notifications/useSelectors";
import { useTemplateMetadataFromIdAUTOFETCH } from "../store/templateMetadata/hooks/useSelectors";

export const VOID_NAVIGATION_CALLBACK = (l: Location) => l;

type OverviewTemplateParams = {
  templateId: TemplateId;
  templateType: TemplateType;
  imperativeSor?: SOR;
};

export type OverviewTemplateRouteState = {
  sor?: SOR;
  fullTextQuery?: string;
  imperativeSor?: SOR; // I think this is not being used anymore. But the idea is being able to create a link to a template with predefined filtering
};

export const useQueryParams = () => {
  const { search } = useLocation();
  return new URLSearchParams(search);
};

const createReportingServicesLink = (templateId: number) =>
  `/reporting-services/${templateId}`;

const getCorrectOverviewPath = (
  templateId: number,
  templateType: TemplateType
) => {
  switch (templateType) {
    case "hwrAnnualEval":
      return createAnnualEvalRootLink(templateId);
    case "hwrPotentialEval":
      return createPotentialEvalRootLink(templateId);
    case "hwrPersonalDevPlan":
      return createPersonalDevPlanRootLink(templateId);
    case "hwrAdaptationPlan":
      return createAdaptationPlanRootLink(templateId);
    case "ssrs":
      return createReportingServicesLink(templateId);
    default:
      return `/overview/${templateId}`;
  }
};

export const getLinkToOverviewTemplate = ({
  templateId,
  imperativeSor = undefined,
  templateType,
}: OverviewTemplateParams) => {
  const pathname = getCorrectOverviewPath(templateId, templateType);

  // I think this is not being used anymore. But the idea is being able to create a link to a template with predefined filtering
  const state: OverviewTemplateRouteState = { imperativeSor };

  return { pathname, state };
};

type getLinkToOverviewTemplateNotificationViewParams = {
  templateId: TemplateId;
  rowsPerPage: number;
  keysOfDisplayedRows: number[];
  primaryColumnName: string;
  currentPage?: number;
};

const createLinkSorFromIds = (
  keysOfRows: number[],
  columnIdentifier: string
): SOR => {
  const createWhereItem = (rowKey: number, index: number): WhereItem => {
    const gate = index === 0 ? Gate.And : Gate.Or;
    return produce(whereItemPLACEHOLDER, (draft) => {
      draft.gate = gate;
      draft.source.columnIdentifier = columnIdentifier;
      draft.relation.type = RelationType.NumberValue;
      draft.searchValue.numberVal = rowKey;
    });
  };

  return produce(EMPTY_SOR, (draft) => {
    const whereItems = keysOfRows.map(createWhereItem);
    draft.search = whereItems;
  });
};

export const getLinkToOverviewTemplateNotificationView = ({
  keysOfDisplayedRows,
  primaryColumnName,
  ...rest
}: getLinkToOverviewTemplateNotificationViewParams) => {
  const sor = createLinkSorFromIds(keysOfDisplayedRows, primaryColumnName);

  return getLinkToOverviewTemplate({
    imperativeSor: sor,
    ...rest,
    templateType: "overview",
  });
};

// Vrátí link na přehledovou tabulku, kde jsou vyfiltrovaný notifikovaný řádky
type useOverviewTemplateNotificationViewLinkParams = {
  templateId: TemplateId;
  currentPage?: number;
  rowsPerPage?: number;
};

export const useOverviewTemplateNotificationViewLink = ({
  templateId,
  rowsPerPage = 10,
  currentPage = 1,
}: useOverviewTemplateNotificationViewLinkParams) => {
  const keysOfDisplayedRows =
    useNotificationRowKeysForTemplate(templateId).map(Number);

  const { keyColName = "" } =
    useTemplateMetadataFromIdAUTOFETCH(templateId) || {};

  return getLinkToOverviewTemplateNotificationView({
    keysOfDisplayedRows,
    currentPage,
    templateId,
    rowsPerPage,
    primaryColumnName: keyColName,
  });
};

export type TableRowActionParams = {
  rowKeyValue: string | number;
  templateId?: string | number | null;
  overviewId?: string | number | null;
  templateType?: TemplateType | undefined;
  activeItemTemplateId?: number;
  templateItemId?: number;
};

export type OverviewTableState = {
  sor?: SOR;
  fulltextQuery?: string;
};

export const getEditTableRowURL = ({
  templateId,
  rowKeyValue,
  templateType = "detail",
  overviewId,
  activeItemTemplateId,
  templateItemId,
}: TableRowActionParams) => {
  if (!templateId) return "";
  const type = templateType === "multiDetail" ? "multi-edit" : "edit";
  const overviewIdPart = overviewId ? `?overviewId=${overviewId}` : "";
  const templateItemIdPart = templateItemId
    ? `&overviewDataTemplateItemId=${templateItemId}`
    : "";
  const activeTemplate = activeItemTemplateId
    ? `&${TableRowQueryParams.ACTIVE_TEMPLATE}=${activeItemTemplateId}`
    : "";
  return `/${type}/${templateId}/${rowKeyValue}${overviewIdPart}${activeTemplate}${templateItemIdPart}`;
};

export const getDetailTableRowURL = ({
  templateId,
  rowKeyValue,
  templateType = "detail",
  overviewId,
  activeItemTemplateId,
  templateItemId,
}: TableRowActionParams) => {
  if (!templateId) return "";
  const type = templateType === "multiDetail" ? "multi-detail" : "detail";
  const overviewIdPart = overviewId ? `?overviewId=${overviewId}` : "";
  const templateItemIdPart = templateItemId
    ? `&overviewDataTemplateItemId=${templateItemId}`
    : "";
  const activeTemplate = activeItemTemplateId
    ? `&${TableRowQueryParams.ACTIVE_TEMPLATE}=${activeItemTemplateId}`
    : "";
  return `/${type}/${templateId}/${rowKeyValue}${overviewIdPart}${activeTemplate}${templateItemIdPart}`;
};

const getQueryAndPathnameFromUrl = (url: string) => {
  const [pathname, query] = url.split("?");
  return [pathname, `?${query}`];
};

export const getTableRowLinks = (
  params: TableRowActionParams,
  state: OverviewTableState
) => {
  const [detailPathname, detailQuery] = getQueryAndPathnameFromUrl(
    getDetailTableRowURL(params)
  );
  const [editPathname, editQuery] = getQueryAndPathnameFromUrl(
    getEditTableRowURL(params)
  );

  return {
    detailLink: {
      pathname: detailPathname,
      search: detailQuery,
      state,
    },
    editLink: {
      pathname: editPathname,
      search: editQuery,
      state,
    },
  };
};

const CURRENT_PAGE = 1;
const ROWS_PER_PAGE = PAGING_OPTION_VALUES[0];
type CreateLinkParams = {
  id: number;
  defaultSearchQuery?: SOR;
  type?: TemplateType;
};
export const createOverviewLink = ({
  id,
  defaultSearchQuery,
  type,
}: CreateLinkParams) =>
  getLinkToOverviewTemplate({
    templateId: id,
    imperativeSor: defaultSearchQuery,
    templateType: type || "overview",
  });
type CreateNotificationViewLinkParams = {
  templateId: TemplateId;
  keysOfDisplayedRows: number[];
  primaryColumnName: string;
};
export const createNotificationViewLink = ({
  templateId,
  keysOfDisplayedRows,
  primaryColumnName,
}: CreateNotificationViewLinkParams) =>
  getLinkToOverviewTemplateNotificationView({
    templateId,
    keysOfDisplayedRows,
    primaryColumnName,
    rowsPerPage: ROWS_PER_PAGE,
    currentPage: CURRENT_PAGE,
  });

export const createNotificationTemplatesViewLink = (dataIds: number[]) => {
  const pathname = "/administration/templates";
  const sor = createLinkSorFromIds(dataIds, "ID");
  const state: OverviewTemplateRouteState = { imperativeSor: sor };

  return { pathname, state };
};

export const createDataSourcesViewLink = (dataIds: number[]) => {
  const pathname = "/administration/data-sources";
  const sor = createLinkSorFromIds(dataIds, "ID");
  const state: OverviewTemplateRouteState = { imperativeSor: sor };

  return { pathname, state };
};

export const getLinkToTemplateBuilder = (templateId: TemplateId) =>
  `/administration/templates/template-builder/${templateId}/presentation-model`;

export const getLinkToHegMappingPage = (datasourceId: DataSourceId) =>
  `/heg-mapping/${datasourceId}`;

export const getLinkToCreateDetail = (
  templateId: number,
  overviewId: number
): string =>
  `/create/${templateId}?${TableRowCreateQueryParams.overviewId}=${overviewId}`;

export const getLinkToCopyDetail = (
  templateId: number,
  overviewId: number,
  rowKeyToCopy: number
): string =>
  `${getLinkToCreateDetail(templateId, overviewId)}&${
    TableRowCreateQueryParams.rowIdToCopy
  }=${rowKeyToCopy}`;

export const useGoBackFromDetailAction = (): VoidFnc => {
  const push = useHistoryPush();
  const location = useLocation();
  const locationHistory = useLocationHistory();

  return (): void => {
    const goBackUrl = getGoBackUrl(location, locationHistory);

    push(goBackUrl);
  };
};
